import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { MediaCard } from '../../media/shared/MediaCard';
import type { CompetitionElementsData, ElementStatus } from './data/api';

const STATUS_ORDER: ElementStatus[] = ['never_used', 'rarely_used', 'well_covered'];
const SKELETON_TILE_COUNT = 4;

const STATUS_META: Record<
  ElementStatus,
  { label: string; definition: string; paletteColor: 'info' | 'warning' | 'success' }
> = {
  never_used: {
    label: 'Never used',
    definition: 'Competitors run it. None of your tagged creatives do.',
    paletteColor: 'info',
  },
  rarely_used: {
    label: 'Rarely used',
    definition: 'Under 5% of your tagged creatives. Tried, never scaled.',
    paletteColor: 'warning',
  },
  well_covered: {
    label: 'Well covered',
    definition: '5% or more of your tagged creatives already use it.',
    paletteColor: 'success',
  },
};

interface CompetitionElementsCoveragePanelProps {
  summary: CompetitionElementsData['summary'] | undefined;
  loading: boolean;
  selected: ElementStatus[];
  onToggle: (status: ElementStatus) => void;
}

function StatCard({
  status,
  count,
  share,
  selected,
  onToggle,
}: {
  status: ElementStatus;
  count: number;
  share: number;
  selected: boolean;
  onToggle: (status: ElementStatus) => void;
}): ReactNode {
  const { label, definition, paletteColor } = STATUS_META[status];
  return (
    <MediaCard
      onClick={() => onToggle(status)}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
        p: 2,
        borderColor: selected ? theme.palette[paletteColor].main : theme.palette.border.default,
        backgroundColor: selected ? theme.palette[paletteColor].bg : 'background.paper',
        '&:hover': { borderColor: theme.palette[paletteColor].main },
      })}
    >
      <Typography variant="h2">{count}</Typography>
      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Box
        sx={(theme) => ({
          height: 6,
          borderRadius: 1,
          backgroundColor: theme.palette[paletteColor].bg,
          overflow: 'hidden',
        })}
      >
        <Box
          sx={(theme) => ({
            width: `${share * 100}%`,
            height: '100%',
            backgroundColor: theme.palette[paletteColor].main,
          })}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {Math.round(share * 100)}%
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {definition}
      </Typography>
    </MediaCard>
  );
}

export function CompetitionElementsCoveragePanel({
  summary,
  loading,
  selected,
  onToggle,
}: CompetitionElementsCoveragePanelProps): ReactNode {
  if (loading || summary === undefined) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        {Array.from(
          { length: SKELETON_TILE_COUNT },
          (_, index) => `coverage-skeleton-${index}`,
        ).map((id) => (
          <Skeleton key={id} variant="rounded" height={140} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
      <MediaCard sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          How to read this table
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Usage is the share of tagged creatives carrying the tag.
        </Typography>
      </MediaCard>
      {STATUS_ORDER.map((status) => (
        <StatCard
          key={status}
          status={status}
          count={summary[status].count}
          share={summary[status].share}
          selected={selected.includes(status)}
          onToggle={onToggle}
        />
      ))}
    </Box>
  );
}
